import type {
  CellOverlay,
  CellSource,
  CellTower,
  HealthSnapshot,
  TripDetails,
  TripId,
  TripStats,
  TripSummary,
} from "../types";

const HEADER_BYTES = 60;
const MAGIC = "TRIP";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, { method: "GET", signal });
  if (!response.ok) throw new ApiError(`Request failed with status ${response.status}`, response.status);
  try {
    return (await response.json()) as T;
  } catch {
    throw new ApiError("The server returned invalid JSON", response.status);
  }
}

export function decodeTrip(buffer: ArrayBuffer): TripDetails {
  if (buffer.byteLength < HEADER_BYTES) throw new Error("Trip payload is truncated");
  const bytes = new Uint8Array(buffer, 0, 4);
  const magic = String.fromCharCode(...bytes);
  const view = new DataView(buffer);
  if (magic !== MAGIC) throw new Error("Unsupported trip payload");
  const flags = view.getUint32(4, true);
  const count = view.getUint32(8, true);
  const expectedBytes = HEADER_BYTES + count * 17;
  if (buffer.byteLength !== expectedBytes) {
    throw new Error("Trip payload has an invalid length");
  }
  const positionsOffset = HEADER_BYTES;
  const timestampsOffset = positionsOffset + count * 8;
  const joysOffset = timestampsOffset + count * 4;
  const flagsOffset = joysOffset + count * 4;
  const rawBounds = [
    view.getFloat64(28, true),
    view.getFloat64(36, true),
    view.getFloat64(44, true),
    view.getFloat64(52, true),
  ];
  return {
    positions: new Float32Array(buffer, positionsOffset, count * 2),
    timestamps: new Uint32Array(buffer, timestampsOffset, count),
    joys: new Float32Array(buffer, joysOffset, count),
    flags: new Uint8Array(buffer, flagsOffset, count),
    count,
    cursor: view.getBigUint64(12, true),
    baseCursor: view.getBigUint64(20, true),
    delta: Boolean(flags & 1),
    finished: Boolean(flags & 2),
    bounds: count
      ? {
          minLongitude: rawBounds[0]!,
          minLatitude: rawBounds[1]!,
          maxLongitude: rawBounds[2]!,
          maxLatitude: rawBounds[3]!,
        }
      : null,
  };
}

export interface TripsApi {
  getTrips(signal?: AbortSignal): Promise<TripSummary[]>;
  getTrip(id: TripId, after?: bigint, signal?: AbortSignal): Promise<TripDetails | null>;
  getCells(id: TripId, through: bigint, signal?: AbortSignal): Promise<CellOverlay>;
  getStats?(id: TripId, through: bigint, signal?: AbortSignal): Promise<TripStats>;
  getHealth?(id: TripId, signal?: AbortSignal): Promise<HealthSnapshot>;
  getLiveUrl(id: TripId, subscription: LiveSubscription): string;
}

export interface LiveSubscription {
  after: bigint;
  cellsAfter?: bigint;
  statsAfter?: bigint;
  healthAfter: bigint;
}

export function decodeStats(buffer: ArrayBuffer): TripStats {
  if (buffer.byteLength < 32 || String.fromCharCode(...new Uint8Array(buffer, 0, 4)) !== "STAT")
    throw new Error("Invalid stats payload");
  const view = new DataView(buffer),
    flags = view.getUint32(4, true),
    count = view.getUint32(8, true);
  if (buffer.byteLength !== 32 + count * 34) throw new Error("Invalid stats payload length");
  let offset = 32;
  const floats = () => {
    const result = new Float32Array(buffer, offset, count);
    offset += count * 4;
    return result;
  };
  const altitudes = floats(),
    accuracies = floats(),
    speeds = floats(),
    batteries = floats(),
    cn0 = floats(),
    pressures = floats();
  const steps = new BigUint64Array(buffer, offset, count);
  offset += count * 8;
  const satellitesVisible = new Uint8Array(buffer, offset, count);
  offset += count;
  const satellitesUsed = new Uint8Array(buffer, offset, count);
  return {
    altitudes,
    accuracies,
    speeds,
    batteries,
    cn0,
    pressures,
    steps,
    satellitesVisible,
    satellitesUsed,
    count,
    cursor: view.getBigUint64(16, true),
    baseCursor: view.getBigUint64(24, true),
    delta: Boolean(flags & 1),
    finished: Boolean(flags & 2),
  };
}

const healthMetrics = [
  "",
  "heart_rate_bpm",
  "speed_mps",
  "power_w",
  "steps",
  "distance_m",
  "elevation_gain_m",
  "floors",
  "active_calories_kcal",
  "total_calories_kcal",
  "oxygen_percent",
  "respiratory_rate",
];

export function decodeHealth(buffer: ArrayBuffer): HealthSnapshot {
  if (buffer.byteLength < 32 || String.fromCharCode(...new Uint8Array(buffer, 0, 4)) !== "HLTH")
    throw new Error("Invalid health payload");
  const view = new DataView(buffer),
    flags = view.getUint32(4, true);
  const upsertCount = view.getUint32(8, true),
    deletionCount = view.getUint32(12, true);
  const decoder = new TextDecoder();
  let offset = 32;
  const textAt = (length: number) => {
    if (offset + length > buffer.byteLength) throw new Error("Truncated health payload");
    const value = decoder.decode(new Uint8Array(buffer, offset, length));
    offset += length;
    return value;
  };
  const samples = [] as HealthSnapshot["samples"];
  for (let i = 0; i < upsertCount; i++) {
    if (offset + 29 > buffer.byteLength) throw new Error("Truncated health sample");
    const metric = healthMetrics[view.getUint8(offset)];
    if (!metric) throw new Error("Unknown health metric");
    const sourceLength = view.getUint16(offset + 1, true),
      recordLength = view.getUint16(offset + 3, true);
    const start_time = Number(view.getBigUint64(offset + 5, true));
    const end_time = Number(view.getBigUint64(offset + 13, true));
    const value = view.getFloat64(offset + 21, true);
    offset += 29;
    samples.push({
      metric,
      start_time,
      end_time,
      value,
      source_package: textAt(sourceLength),
      record_id: textAt(recordLength),
    });
  }
  const deletions = [] as HealthSnapshot["deletions"];
  for (let i = 0; i < deletionCount; i++) {
    if (offset + 4 > buffer.byteLength) throw new Error("Truncated health deletion");
    const sourceLength = view.getUint16(offset, true),
      recordLength = view.getUint16(offset + 2, true);
    offset += 4;
    deletions.push({ source_package: textAt(sourceLength), record_id: textAt(recordLength) });
  }
  if (offset !== buffer.byteLength) throw new Error("Invalid health payload length");
  return {
    samples,
    deletions,
    delta: Boolean(flags & 1),
    base_revision: view.getBigUint64(16, true),
    revision: view.getBigUint64(24, true),
  };
}

const align4 = (value: number) => (value + 3) & ~3;
export function decodeCells(buffer: ArrayBuffer): CellOverlay {
  if (buffer.byteLength < 40 || String.fromCharCode(...new Uint8Array(buffer, 0, 4)) !== "CELL")
    throw new Error("Invalid cell payload");
  const view = new DataView(buffer),
    flags = view.getUint32(4, true),
    towerCount = view.getUint32(8, true),
    linkCount = view.getUint32(12, true);
  const towers: CellTower[] = [];
  const positions = 40,
    cells = positions + towerCount * 8;
  const mcc = cells + towerCount * 8,
    net = mcc + towerCount * 2,
    area = net + towerCount * 2,
    radio = area + towerCount * 4;
  const points = align4(radio + towerCount),
    towerIndices = points + linkCount * 4,
    dbm = towerIndices + linkCount * 4,
    levels = dbm + linkCount * 2;
  const legacyEnd = levels + linkCount,
    enriched = Boolean(flags & 2),
    sourceCount = view.getUint32(36, true);
  for (let i = 0; i < towerCount; i++)
    towers.push({
      longitude: view.getFloat32(positions + i * 8, true),
      latitude: view.getFloat32(positions + i * 8 + 4, true),
      cell: view.getBigUint64(cells + i * 8, true),
      mcc: view.getUint16(mcc + i * 2, true),
      net: view.getUint16(net + i * 2, true),
      area: view.getUint32(area + i * 4, true),
      radio: view.getUint8(radio + i),
    });
  let sourceIndices = new Uint16Array(linkCount),
    connection = new Uint8Array(linkCount),
    linkFlags = new Uint8Array(linkCount);
  let quality = new Int16Array(linkCount).fill(-32768),
    noise = new Int16Array(linkCount).fill(-32768);
  let age = new Uint32Array(linkCount),
    channels = new Uint32Array(linkCount),
    pci = new Uint16Array(linkCount);
  let bands = new Uint16Array(linkCount),
    bandwidth = new Uint32Array(linkCount);
  const sources: CellSource[] = [];
  if (!enriched) throw new Error("Unsupported cell payload");
  const sourceAt = align4(legacyEnd),
    connectionAt = sourceAt + linkCount * 2,
    flagsAt = connectionAt + linkCount;
  const qualityAt = align4(flagsAt + linkCount),
    noiseAt = qualityAt + linkCount * 2,
    ageAt = align4(noiseAt + linkCount * 2);
  const channelAt = ageAt + linkCount * 4,
    pciAt = channelAt + linkCount * 4,
    bandAt = pciAt + linkCount * 2;
  const bandwidthAt = align4(bandAt + linkCount * 2),
    sourceRecordsAt = bandwidthAt + linkCount * 4,
    namesAt = sourceRecordsAt + sourceCount * 16;
  if (namesAt > buffer.byteLength) throw new Error("Invalid enriched cell payload length");
  sourceIndices = new Uint16Array(buffer, sourceAt, linkCount);
  connection = new Uint8Array(buffer, connectionAt, linkCount);
  linkFlags = new Uint8Array(buffer, flagsAt, linkCount);
  quality = new Int16Array(buffer, qualityAt, linkCount);
  noise = new Int16Array(buffer, noiseAt, linkCount);
  age = new Uint32Array(buffer, ageAt, linkCount);
  channels = new Uint32Array(buffer, channelAt, linkCount);
  pci = new Uint16Array(buffer, pciAt, linkCount);
  bands = new Uint16Array(buffer, bandAt, linkCount);
  bandwidth = new Uint32Array(buffer, bandwidthAt, linkCount);
  let nameAt = namesAt;
  for (let i = 0; i < sourceCount; i++) {
    const offset = sourceRecordsAt + i * 16,
      nameLength = view.getUint16(offset + 14, true);
    if (nameAt + nameLength > buffer.byteLength) throw new Error("Invalid cell source name");
    const sourceFlags = view.getUint8(offset + 12),
      carrierName = new TextDecoder().decode(new Uint8Array(buffer, nameAt, nameLength));
    nameAt += nameLength;
    sources.push({
      subscriptionId: view.getInt32(offset, true),
      slotIndex: view.getInt16(offset + 4, true),
      carrierId: view.getInt32(offset + 6, true),
      networkType: view.getUint8(offset + 10),
      displayOverride: view.getUint8(offset + 11),
      carrierName,
      activeData: Boolean(sourceFlags & 1),
      roaming: Boolean(sourceFlags & 2),
      embedded: Boolean(sourceFlags & 4),
      opportunistic: Boolean(sourceFlags & 8),
    });
  }
  if (nameAt !== buffer.byteLength || sourceIndices.some((value) => value >= sourceCount))
    throw new Error("Invalid cell source table");
  return {
    towers,
    pointIndices: new Uint32Array(buffer, points, linkCount),
    towerIndices: new Uint32Array(buffer, towerIndices, linkCount),
    dbm: new Int16Array(buffer, dbm, linkCount),
    levels: new Uint8Array(buffer, levels, linkCount),
    sources,
    sourceIndices,
    connection,
    linkFlags,
    quality,
    noise,
    age,
    channels,
    pci,
    bands,
    bandwidth,
    cursor: view.getBigUint64(16, true),
    baseCursor: view.getBigUint64(24, true),
    basePointIndex: view.getUint32(32, true),
    delta: Boolean(flags & 1),
  };
}

export function createTripsApi(baseUrl: string): TripsApi {
  const normalizedBase = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  return {
    getTrips: (signal) => getJson<TripSummary[]>(normalizedBase, signal),
    getTrip: async (id, after, signal) => {
      const url = new URL(encodeURIComponent(String(id)), normalizedBase);
      if (after !== undefined) url.searchParams.set("after", after.toString());
      const response = await fetch(url, {
        method: "GET",
        signal,
        headers: { Accept: "application/octet-stream" },
      });
      if (response.status === 204 || response.status === 304) return null;
      if (!response.ok) throw new ApiError(`Request failed with status ${response.status}`, response.status);
      try {
        return decodeTrip(await response.arrayBuffer());
      } catch (error) {
        throw new ApiError(error instanceof Error ? error.message : "Invalid trip payload", response.status);
      }
    },
    getCells: async (id, through, signal) => {
      const url = new URL(`${encodeURIComponent(String(id))}/cells`, normalizedBase);
      url.searchParams.set("through", through.toString());
      const response = await fetch(url, { signal, headers: { Accept: "application/octet-stream" } });
      if (!response.ok) throw new ApiError(`Request failed with status ${response.status}`, response.status);
      return decodeCells(await response.arrayBuffer());
    },
    getStats: async (id, through, signal) => {
      const url = new URL(`${encodeURIComponent(String(id))}/stats`, normalizedBase);
      url.searchParams.set("through", through.toString());
      const response = await fetch(url, { signal, headers: { Accept: "application/octet-stream" } });
      if (!response.ok) throw new ApiError(`Request failed with status ${response.status}`, response.status);
      return decodeStats(await response.arrayBuffer());
    },
    getHealth: async (id, signal) => {
      const response = await fetch(new URL(`${encodeURIComponent(String(id))}/health`, normalizedBase), {
        signal,
        headers: { Accept: "application/octet-stream" },
      });
      if (!response.ok) throw new ApiError(`Request failed with status ${response.status}`, response.status);
      return decodeHealth(await response.arrayBuffer());
    },
    getLiveUrl: (id, subscription) => {
      const url = new URL(`${encodeURIComponent(String(id))}/live`, normalizedBase);
      url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
      url.searchParams.set("after", subscription.after.toString());
      if (subscription.cellsAfter !== undefined) {
        url.searchParams.set("cells", "1");
        url.searchParams.set("cells_after", subscription.cellsAfter.toString());
      }
      if (subscription.statsAfter !== undefined) {
        url.searchParams.set("stats", "1");
        url.searchParams.set("stats_after", subscription.statsAfter.toString());
      }
      url.searchParams.set("health", "1");
      url.searchParams.set("health_after", subscription.healthAfter.toString());
      return url.toString();
    },
  };
}
